import { Stack, Typography } from '@mui/material';

import { Button } from '@/components/ui/button.tsx';
import { Tooltip } from '@/components/ui/tooltip.tsx';

import { experteAntworten, type Leistung, vivaStatus } from '../../../dtos';
import { getExperteName } from '../../../utils';
import { AusschreibungLink } from '../AusschreibungLink.tsx';
import { AusschreibungStatusTag } from '../AusschreibungStatusTag.tsx';
import { useLeistungStatusMutation } from '../hooks/useLeistungStatusMutation.ts';
import { useVorschlaegeInAngebotUebertragenMutation } from '../hooks/useVorschlaegeInAngebotUebertragenMutation.ts';
import { LabelValue } from '../LabelValue';
import { LeistungIndexTag } from '../ThemaHeader/LeistungIndexTag';
import { isPruefenFlowAktiv } from './AusschreibungRueckmeldungenCard.utils';
import { content } from './AusschreibungsprozessCard.content';
import { ManuellerAusschreibungsprozessInfo } from './ManuellerAusschreibungsprozessInfo';
import { isTerminvorschlaegePruefenEnabled } from './terminvorschlaegePruefenFeature.ts';

type Props = {
  readonly leistung: Leistung;
  readonly showManuellerAusschreibungsprozessInfo?: boolean;
  readonly showStatusActions?: boolean;
};

export const AusschreibungsprozessCard = ({
  leistung,
  showManuellerAusschreibungsprozessInfo,
  showStatusActions = true,
}: Props) => {
  const { ausschreibung } = leistung;

  const angebotAngenommenVon = ausschreibung?.experteAntworten.find(
    (experteAntwort) => experteAntwort.antwort === experteAntworten.ANGEBOT_ANGENOMMEN,
  )?.experte;

  const vertragStorniertFuer = ausschreibung?.vertragStorniertFuer;

  return (
    <div className="relative h-full rounded-sm border border-default p-16">
      <Stack spacing={2}>
        <LeistungIndexTag index={leistung.index} />
        <Typography fontSize="14px" fontWeight="500">
          {content.titel}
        </Typography>
        {showManuellerAusschreibungsprozessInfo && <ManuellerAusschreibungsprozessInfo />}
        <div>
          <Stack spacing={1.5}>
            <LabelValue label="Leistungs-ID" value={<AusschreibungLink leistung={leistung} />} />
            {ausschreibung && (
              <>
                <LabelValue label="Status" value={<AusschreibungStatusTag status={ausschreibung.status} />} />
                {angebotAngenommenVon && (
                  <LabelValue label="Angebot angenommen von" value={getExperteName(angebotAngenommenVon)} />
                )}
                {vertragStorniertFuer && (
                  <LabelValue label="Vertrag storniert für" value={getExperteName(vertragStorniertFuer)} />
                )}
              </>
            )}
          </Stack>
        </div>
        {showStatusActions && <AusschreibungsprozessCardStatusButton leistung={leistung} />}
      </Stack>
    </div>
  );
};

const AusschreibungsprozessCardStatusButton = ({ leistung }: { readonly leistung: Leistung }) => {
  const statusMutation = useLeistungStatusMutation();
  const uebertragenMutation = useVorschlaegeInAngebotUebertragenMutation();
  const canUebertragen = isTerminvorschlaegePruefenEnabled() && leistung.canVorschlaegeInAngebotUebertragen;

  if (
    leistung.ausschreibung?.experteAntworten.some(
      (experteAntwort) => experteAntwort.antwort === experteAntworten.ANGEBOT_ANGENOMMEN,
    )
  ) {
    return null;
  }

  if (isPruefenFlowAktiv(leistung) && !canUebertragen) {
    return (
      <Button
        label={content.vorschlaegeNichtGeprueft}
        disabled
        variant="tertiary"
        size="compact"
        className="self-start"
      />
    );
  }

  if (leistung.ausschreibung?.hatAusstehendeAngebote === true) {
    return (
      <Tooltip
        trigger={
          <span style={{ alignSelf: 'flex-start' }}>
            <Button label={content.leistungUmplanen} disabled variant="tertiary" size="compact" />
          </span>
        }
        arrowPosition="bottom"
      >
        {content.hatAusstehendeAngeboteHint}
      </Tooltip>
    );
  }

  if (canUebertragen) {
    return (
      <Button
        label={content.vorschlaegeInAngebotUebertragen}
        variant="primary"
        size="compact"
        className="self-start"
        disabled={uebertragenMutation.isPending}
        onClick={() => {
          uebertragenMutation.mutate({ leistungId: leistung.leistungId });
        }}
      />
    );
  }

  if (leistung.statusTransitions.includes(vivaStatus.IM_AUSSCHREIBUNGSPORTAL)) {
    return (
      <Button
        label={content.inAusschreibungUebertragen}
        variant="tertiary"
        size="compact"
        className="self-start"
        disabled={statusMutation.isPending}
        loading={statusMutation.isPending}
        onClick={() => {
          statusMutation.mutate({
            leistungId: leistung.leistungId,
            newStatus: vivaStatus.IM_AUSSCHREIBUNGSPORTAL,
          });
        }}
      />
    );
  }

  if (leistung.statusTransitions.includes(vivaStatus.IN_PLANUNG)) {
    return (
      <Button
        label={content.leistungUmplanen}
        variant="tertiary"
        size="compact"
        className="self-start"
        disabled={statusMutation.isPending}
        loading={statusMutation.isPending}
        onClick={() => {
          statusMutation.mutate({
            leistungId: leistung.leistungId,
            newStatus: vivaStatus.IN_PLANUNG,
          });
        }}
      />
    );
  }
};
